import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import KPICard from './KPICard';
import DashboardFilters from './DashboardFilters';
import DashboardErrorBoundary from './DashboardErrorBoundary';
import CommonChart from '../common/CommonChart';
import {
    getDashboardChartTypes,
    applyDashboardChartTypes,
    getDashboardVisibleWidgets,
    applyDashboardVisibleWidgets,
    getDashboardCustomTitles,
    applyDashboardCustomTitles
} from '../common/CommonChart';
import {
    updateDashboardFilters,
    refreshDashboard,
    fetchCompanies,
    fetchSellersByCompanyIds,
    saveDashboardPreference,
    loadDefaultDashboardPreference,
    fetchAllDashboardPreferences,
    deleteDashboardPreference,
    applyDashboardPreference
} from '../../store/actions/dashboardAnalytics.actions';
import { getWidgetsForRole } from '../../constants/widgetRegistry';
import { CATEGORY_LABELS, resolveRoleId, ROLE_TYPES } from '../../constants/roleConstants';
import './Dashboard.css';

// Widget Configuration is now loaded dynamically based on user role from widgetRegistry.js
// This ensures users only see widgets appropriate for their role

// Session storage key for persisting dashboard layout/customizations across page refresh.
// Holds chart types, visible widgets, custom titles and widget order so that F5/refresh
// keeps the user's last working dashboard instead of resetting to the default preset.
const DASHBOARD_SESSION_SNAPSHOT_KEY = 'dashboardSessionSnapshot';

const readSessionSnapshot = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(DASHBOARD_SESSION_SNAPSHOT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

const writeSessionSnapshot = (patch) => {
    if (typeof window === 'undefined') return;
    try {
        const existing = readSessionSnapshot() || {};
        const merged = { ...existing, ...patch };
        sessionStorage.setItem(DASHBOARD_SESSION_SNAPSHOT_KEY, JSON.stringify(merged));
    } catch (e) {
        // ignore quota errors
    }
};

// =====================================================================
// PDF export helpers
// Module-level helpers used by handleExportPdf. They are split into small
// pure functions so that handleExportPdf itself stays a thin orchestrator
// (keeping cognitive complexity low).
// =====================================================================
const PDF_LAYOUT_CONSTS = { margin: 32, blockGap: 10, pairGap: 8 };

// ---- Filter summary formatting (avoids nested template literals) ----
const formatCompanyLine = (companyId, companies) => {
    if (!companyId) return 'Company: All';
    const company = (companies || []).find((c) => Number(c.Id) === companyId);
    const fallbackLabel = `#${companyId}`;
    const name = company?.name || company?.Name || fallbackLabel;
    return `Company: ${name}`;
};

const formatSellerLine = (sellerId, sellers) => {
    if (!sellerId) return 'Seller: All';
    const seller = (sellers || []).find((s) => Number(s.id) === sellerId);
    const fallbackLabel = `#${sellerId}`;
    const name = seller?.newSellerName || seller?.sellerName || seller?.sellerCode || fallbackLabel;
    return `Seller: ${name}`;
};

const formatPeriodLine = (period, from, to) => {
    if (from && to) {
        const range = `${from} - ${to}`;
        return period ? `Period: ${period} (${range})` : `Period: ${range}`;
    }
    return period ? `Period: ${period}` : null;
};

const formatDateValue = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString();
};

// ---- DOM block collection ----
const appendWidgetGridBlocks = (widgetGrid, blocks) => {
    const children = Array.from(widgetGrid.children);
    let i = 0;
    while (i < children.length) {
        const child = children[i];
        if (child.classList.contains('category-break')) {
            blocks.push({ kind: 'category', nodes: [child] });
            i += 1;
            continue;
        }
        if (!child.classList.contains('widget-col')) {
            i += 1;
            continue;
        }
        const next = children[i + 1];
        const isPair = next?.classList?.contains('widget-col');
        if (isPair) {
            blocks.push({ kind: 'widget-pair', nodes: [child, next] });
            i += 2;
        } else {
            blocks.push({ kind: 'widget-single', nodes: [child] });
            i += 1;
        }
    }
};

const appendStaticRowBlocks = (node, blocks) => {
    const seen = new Set();
    blocks.forEach((b) => b.nodes.forEach((n) => seen.add(n)));
    const rows = node.querySelectorAll(':scope > .chart-row:not(.widget-grid-row), :scope > .row.chart-row:not(.widget-grid-row)');
    rows.forEach((row) => {
        if (!seen.has(row)) {
            blocks.push({ kind: 'row', nodes: [row] });
            seen.add(row);
        }
    });
};

const collectDashboardBlocks = (node) => {
    const blocks = [];
    const kpiStrip = node.querySelector('.kpi-strip');
    if (kpiStrip) blocks.push({ kind: 'row', nodes: [kpiStrip] });
    const widgetGrid = node.querySelector('.widget-grid-row');
    if (widgetGrid) appendWidgetGridBlocks(widgetGrid, blocks);
    appendStaticRowBlocks(node, blocks);
    return blocks;
};

// ---- Snapshot to canvas ----
const captureDashboardBlocks = async (blocks) => {
    const opts = { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false };
    const captured = [];
    for (const block of blocks) {
        // eslint-disable-next-line no-await-in-loop
        const canvases = await Promise.all(block.nodes.map((el) => html2canvas(el, opts)));
        captured.push({ kind: block.kind, canvases });
    }
    return captured;
};

// ---- PDF layout / placement ----
const createPdfLayout = (pdf) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const { margin, blockGap, pairGap } = PDF_LAYOUT_CONSTS;
    const contentWidth = pageWidth - margin * 2;
    return {
        pageWidth,
        pageHeight,
        margin,
        contentWidth,
        pairGap,
        blockGap,
        halfWidth: (contentWidth - pairGap) / 2,
        bottomLimit: pageHeight - margin,
        fullPageHeight: pageHeight - margin * 2,
    };
};

const computeRenderedHeight = (canvas, width) => (canvas.height * width) / canvas.width;

const placeCanvas = (pdf, canvas, x, y, width) => {
    const height = computeRenderedHeight(canvas, width);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    pdf.addImage(dataUrl, 'JPEG', x, y, width, height, undefined, 'FAST');
    return height;
};

const buildSliceCanvas = (canvas, srcY, sliceHeightPx) => {
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = sliceHeightPx;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, srcY, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
    return out;
};

const placeSlicedFullWidth = (pdf, canvas, atY, layout) => {
    const { margin, contentWidth, bottomLimit } = layout;
    const pxPerPt = canvas.width / contentWidth;
    let remainingPx = canvas.height;
    let srcY = 0;
    let pageY = atY;
    while (remainingPx > 0) {
        const availablePt = bottomLimit - pageY;
        const sliceHeightPx = Math.min(remainingPx, Math.max(1, availablePt * pxPerPt));
        const sliceCanvas = buildSliceCanvas(canvas, srcY, sliceHeightPx);
        const sliceHeightPt = sliceHeightPx / pxPerPt;
        pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin, pageY, contentWidth, sliceHeightPt, undefined, 'FAST');
        remainingPx -= sliceHeightPx;
        srcY += sliceHeightPx;
        if (remainingPx > 0) {
            pdf.addPage();
            pageY = margin;
        } else {
            return pageY + sliceHeightPt;
        }
    }
    return pageY;
};

const computeBlockHeight = (block, layout) => {
    if (block.kind === 'widget-pair') {
        const [c1, c2] = block.canvases;
        return Math.max(
            computeRenderedHeight(c1, layout.halfWidth),
            computeRenderedHeight(c2, layout.halfWidth)
        );
    }
    return computeRenderedHeight(block.canvases[0], layout.contentWidth);
};

const placeWidgetPair = (pdf, block, cursorY, layout) => {
    const { margin, halfWidth, pairGap, blockGap, bottomLimit, fullPageHeight, contentWidth } = layout;
    const [c1, c2] = block.canvases;
    const h = computeBlockHeight(block, layout);
    let y = cursorY;
    if (y + h > bottomLimit) {
        pdf.addPage();
        y = margin;
    }
    if (h <= fullPageHeight) {
        placeCanvas(pdf, c1, margin, y, halfWidth);
        placeCanvas(pdf, c2, margin + halfWidth + pairGap, y, halfWidth);
        return y + h + blockGap;
    }
    // Fallback: very tall pair - stack vertically at full width.
    const h1 = placeCanvas(pdf, c1, margin, y, contentWidth);
    y += h1 + blockGap;
    if (y + 10 > bottomLimit) {
        pdf.addPage();
        y = margin;
    }
    const h2 = placeCanvas(pdf, c2, margin, y, contentWidth);
    return y + h2 + blockGap;
};

const placeCategoryHeader = (pdf, block, nextBlock, cursorY, layout) => {
    const { margin, contentWidth, blockGap, bottomLimit, fullPageHeight } = layout;
    const canvas = block.canvases[0];
    const headerH = computeRenderedHeight(canvas, contentWidth);
    const companionH = nextBlock ? computeBlockHeight(nextBlock, layout) : 0;
    const needed = headerH + blockGap + Math.min(companionH, fullPageHeight * 0.4);
    let y = cursorY;
    if (y + needed > bottomLimit) {
        pdf.addPage();
        y = margin;
    }
    placeCanvas(pdf, canvas, margin, y, contentWidth);
    return y + headerH + blockGap;
};

const placeFullWidthBlock = (pdf, block, cursorY, layout) => {
    const { margin, contentWidth, blockGap, bottomLimit, fullPageHeight } = layout;
    const canvas = block.canvases[0];
    const h = computeRenderedHeight(canvas, contentWidth);
    let y = cursorY;
    if (y + h <= bottomLimit) {
        placeCanvas(pdf, canvas, margin, y, contentWidth);
        return y + h + blockGap;
    }
    if (h > fullPageHeight) {
        if (y > margin + 1) {
            pdf.addPage();
            y = margin;
        }
        return placeSlicedFullWidth(pdf, canvas, y, layout) + blockGap;
    }
    pdf.addPage();
    y = margin;
    placeCanvas(pdf, canvas, margin, y, contentWidth);
    return y + h + blockGap;
};

const layoutCapturedBlocks = (pdf, captured, startY, layout) => {
    let cursorY = startY;
    for (let i = 0; i < captured.length; i += 1) {
        const block = captured[i];
        if (block.kind === 'widget-pair') {
            cursorY = placeWidgetPair(pdf, block, cursorY, layout);
        } else if (block.kind === 'category') {
            cursorY = placeCategoryHeader(pdf, block, captured[i + 1], cursorY, layout);
        } else {
            cursorY = placeFullWidthBlock(pdf, block, cursorY, layout);
        }
    }
    return cursorY;
};

const drawPdfHeader = (pdf, layout, summaryLines) => {
    const { pageWidth, margin } = layout;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Dashboard Report', margin, margin + 4);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(110);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin, margin + 4, { align: 'right' });
    pdf.setTextColor(0);

    pdf.setFontSize(10);
    let cursorY = margin + 22;
    summaryLines.forEach((line) => {
        pdf.text(line, margin, cursorY);
        cursorY += 13;
    });
    pdf.setDrawColor(220);
    pdf.line(margin, cursorY + 2, pageWidth - margin, cursorY + 2);
    return cursorY + 14;
};

const addFooterPageNumbers = (pdf, layout) => {
    const { pageWidth, pageHeight, margin } = layout;
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
    }
};

const buildPdfFilename = (presetName) => {
    const safeName = (presetName || 'dashboard').replaceAll(/\s+/g, '-').toLowerCase();
    const timestamp = new Date().toISOString().slice(0, 19).replaceAll(/[:T]/g, '-');
    return `${safeName}-${timestamp}.pdf`;
};

const Dashboard = ({ isEnabled = true }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        kpis = {},
        widgets = {},
        filters = {},
        loading = false,
        companies = [],
        sellers = [],
        companiesLoading = false,
        companiesError = null,
        preference = null
    } = useSelector(
        state => state.dashboardAnalytics || {}
    );
    const [showPresetModal, setShowPresetModal] = useState(false);
    const [presetModalMode, setPresetModalMode] = useState('create');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCustomizeModal, setShowCustomizeModal] = useState(false);
    const [presetName, setPresetName] = useState('');
    const [setAsDefault, setSetAsDefault] = useState(true);
    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [localVisibleWidgets, setLocalVisibleWidgets] = useState({});
    const [widgetOrder, setWidgetOrder] = useState({});   // { [key]: index }
    const [draggingKey, setDraggingKey] = useState(null);
    const [dragOverKey, setDragOverKey] = useState(null);
    const [viewAsRole, setViewAsRole] = useState(ROLE_TYPES.SELLER); // Locked to Seller view until other role views are ready
    const [hasPendingDashboardChanges, setHasPendingDashboardChanges] = useState(false);
    const [visibilityVersion, setVisibilityVersion] = useState(0);
    const TOP_STRIP_KPI_VALUE_KEYS = useMemo(() => new Set([
        'totalLabels',
        'outboundOrders',
        'totalSKUs',
        'inboundConsignments',
        'openTasks',
        'walletBalance'
    ]), []);
    // Widget data keys and KPI value keys that represent monetary values.
    // Used to prefix axis ticks, tooltips, and square values with the currency symbol.
    const CURRENCY_DATA_KEYS = useMemo(() => new Set([
        'monthlyRevenueTrend',
        'inventoryValueByCategory',
        'spendTrend',
        'revenueByCompany',
        'refundVolumeByCompany',
        'averageOrderValue',
        'averageShippingCost',
        'shippingCostVarianceAnalysis'
    ]), []);
    const CURRENCY_WIDGET_KEYS = useMemo(() => new Set([
        'Shipping Cost Trend',
        'Inventory by Type',
        'Spend Trend',
        'Revenue by Company',
        'Refund Request Volume',
        'Average Order Value',
        'Average Shipping Cost'
    ]), []);
    const importInputRef = useRef(null);
    // Ref to the dashboard data area (KPI strip + widgets) used by the PDF export.
    const exportAreaRef = useRef(null);
    // Track in-flight PDF export so we can disable the menu item and avoid duplicates.
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    // Ref to always read latest widgetOrder in async callbacks
    const widgetOrderRef = useRef(widgetOrder);
    useEffect(() => { widgetOrderRef.current = widgetOrder; }, [widgetOrder]);

    const currentUser = useMemo(() => {
        const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        return userDetails?.Value || {};
    }, []);

    const getResolvedRoleId = useCallback((user) => resolveRoleId(user), []);

    const currentUserRole = useMemo(() => {
        return getResolvedRoleId(currentUser);
    }, [currentUser, getResolvedRoleId]);

    const currentUserCompanyId = Number(currentUser?.companyId || currentUser?.CompanyId) || null;
    const currentUserSellerFilterId = Number(
        currentUser?.companySellerId ||
        currentUser?.CompanySellerId ||
        currentUser?.sellerId ||
        currentUser?.SellerId
    ) || null;
    const isSellerScopedUser = [ROLE_TYPES.SELLER, ROLE_TYPES.ONBOARDING_SELLER, ROLE_TYPES.ADMIN].includes(Number(currentUserRole));
    const normalizedCurrentCompanyName = String(currentUser?.companyName || currentUser?.CompanyName || '').trim().toLowerCase();
    const normalizedCurrentSellerName = String(currentUser?.sellerName || currentUser?.SellerName || '').trim().toLowerCase();

    const selectedPreset = useMemo(() => {
        return (preference?.allPresets || []).find((preset) => preset.Id === selectedPresetId) || preference?.saved || null;
    }, [preference?.allPresets, preference?.saved, selectedPresetId]);

    const availableCompanies = useMemo(() => {
        if (!isSellerScopedUser) return companies;

        const matchingCompanies = (companies || []).filter((company) =>
            Number(company.Id) === currentUserCompanyId ||
            (normalizedCurrentCompanyName && String(company?.name || '').trim().toLowerCase() === normalizedCurrentCompanyName)
        );
        if (matchingCompanies.length > 0) return matchingCompanies;
        if (!currentUserCompanyId) return [];

        return [{
            Id: currentUserCompanyId,
            name: currentUser?.companyName || currentUser?.CompanyName || 'My Company'
        }];
    }, [companies, currentUser, currentUserCompanyId, isSellerScopedUser, normalizedCurrentCompanyName]);

    const scopedCompanyId = useMemo(() => {
        if (currentUserCompanyId) return currentUserCompanyId;
        return Number(availableCompanies?.[0]?.Id) || null;
    }, [availableCompanies, currentUserCompanyId]);

    const availableSellers = useMemo(() => {
        if (!isSellerScopedUser) return sellers;

        const matchingSellers = (sellers || []).filter((seller) =>
            Number(seller.id) === currentUserSellerFilterId ||
            (normalizedCurrentSellerName && String(seller?.newSellerName || seller?.sellerName || seller?.sellerCode || '').trim().toLowerCase() === normalizedCurrentSellerName)
        );
        if (matchingSellers.length > 0) return matchingSellers;
        if (!currentUserSellerFilterId) return [];

        return [{
            id: currentUserSellerFilterId,
            newSellerName: currentUser?.sellerName || currentUser?.SellerName || currentUser?.sellerCode || 'My Seller'
        }];
    }, [currentUser, currentUserSellerFilterId, isSellerScopedUser, normalizedCurrentSellerName, sellers]);

    const scopedSellerId = useMemo(() => {
        if (currentUserSellerFilterId) return currentUserSellerFilterId;
        return Number(availableSellers?.[0]?.id) || null;
    }, [availableSellers, currentUserSellerFilterId]);

    const canModifySelectedPreset = Boolean(selectedPreset && selectedPreset.Name !== 'Default Dashboard' && selectedPreset.Name !== 'Default');

    useEffect(() => {
        // Skip loading if dashboard is not enabled
        if (!isEnabled) {
            console.log('Dashboard is disabled - skipping data load');
            return;
        }

        // First, try to restore filters from sessionStorage (highest priority)
        let filtersToUse = null;
        const savedFilters = sessionStorage.getItem('dashboardFilters');
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                // Convert date strings back to Date objects
                filtersToUse = {
                    ...parsed,
                    fromDate: parsed.fromDate ? new Date(parsed.fromDate) : null,
                    toDate: parsed.toDate ? new Date(parsed.toDate) : null
                };
                console.log('Restored filters from sessionStorage:', filtersToUse);
            } catch (e) {
                console.error('Failed to parse sessionStorage filters:', e);
            }
        }

        // Try loading saved preference
        dispatch(loadDefaultDashboardPreference()).then((savedPreference) => {
            // Session snapshot (set on every customization) wins over the saved default,
            // so a page refresh shows the user's last working dashboard instead of resetting.
            const snapshot = readSessionSnapshot();
            const chartTypes = snapshot?.chartTypes ?? savedPreference?.chartTypes;
            const visibleWidgets = snapshot?.visibleWidgets ?? savedPreference?.visibleWidgets;
            const customTitles = snapshot?.customTitles ?? savedPreference?.customTitles;
            const widgetOrder = snapshot?.widgetOrder ?? savedPreference?.widgetOrder;

            if (chartTypes) {
                applyDashboardChartTypes(chartTypes);
            }
            if (visibleWidgets) {
                applyDashboardVisibleWidgets(visibleWidgets);
            }
            if (customTitles) {
                applyDashboardCustomTitles(customTitles);
            }
            if (widgetOrder) {
                setWidgetOrder(widgetOrder);
            }
            // Restore last-selected preset and View As role so the dropdowns don't reset on refresh.
            if (snapshot?.selectedPresetId !== undefined && snapshot?.selectedPresetId !== null) {
                setSelectedPresetId(snapshot.selectedPresetId);
            }
            if (snapshot?.viewAsRole !== undefined && snapshot?.viewAsRole !== null) {
                setViewAsRole(snapshot.viewAsRole);
            } else if (filtersToUse?.viewAsRole !== undefined && filtersToUse?.viewAsRole !== null) {
                setViewAsRole(filtersToUse.viewAsRole);
            }
            
            // Use restored filters if available, otherwise use default
            if (filtersToUse) {
                filtersToUse.employeeId = currentUser?.employeeId || 0;
                dispatch(updateDashboardFilters(filtersToUse));
                dispatch(refreshDashboard(filtersToUse));
                console.log('Using restored filters from sessionStorage');
            } else {
                // No saved filters - load with default filters
                const defaultFilters = {
                    ...filters,
                    employeeId: currentUser?.employeeId || 0
                };
                dispatch(refreshDashboard(defaultFilters));
            }
        });
        dispatch(fetchCompanies());
        dispatch(fetchAllDashboardPreferences());
    }, [isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isSellerScopedUser || !scopedCompanyId) return;
        dispatch(fetchSellersByCompanyIds([scopedCompanyId]));
    }, [dispatch, isSellerScopedUser, scopedCompanyId]);

    useEffect(() => {
        // For non-seller-scoped users, reload sellers when a saved company filter exists.
        if (isSellerScopedUser) return;
        if (!filters?.companyId) return;
        dispatch(fetchSellersByCompanyIds([filters.companyId]));
    }, [dispatch, filters?.companyId, isSellerScopedUser]);

    useEffect(() => {
        if (!isSellerScopedUser) return;

        const nextFilters = { ...filters };
        let hasChanges = false;

        if (scopedCompanyId && Number(nextFilters.companyId) !== scopedCompanyId) {
            nextFilters.companyId = scopedCompanyId;
            hasChanges = true;
        }

        if (scopedSellerId && Number(nextFilters.sellerId) !== scopedSellerId) {
            nextFilters.sellerId = scopedSellerId;
            hasChanges = true;
        }

        if (hasChanges) {
            dispatch(updateDashboardFilters(nextFilters));
            dispatch(refreshDashboard(nextFilters));
        }
    }, [dispatch, filters, isSellerScopedUser, scopedCompanyId, scopedSellerId]);

    useEffect(() => {
        if (preference?.saved?.Id) {
            // Only fall back to the default preset if nothing was restored from the session
            // snapshot — otherwise we'd overwrite the user's last-selected dashboard.
            const snapshot = readSessionSnapshot();
            if (snapshot?.selectedPresetId === undefined || snapshot?.selectedPresetId === null) {
                setSelectedPresetId(preference.saved.Id);
            }
        }
    }, [preference?.saved]);

    useEffect(() => {
        const currentVisibility = getDashboardVisibleWidgets();
        setLocalVisibleWidgets(currentVisibility);
    }, [showCustomizeModal]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const syncVisibilityFromPreference = () => {
            setVisibilityVersion((version) => version + 1);
        };

        window.addEventListener('dashboard-visible-widgets-applied', syncVisibilityFromPreference);
        return () => {
            window.removeEventListener('dashboard-visible-widgets-applied', syncVisibilityFromPreference);
        };
    }, []);

    // Persist dashboard customizations to sessionStorage so a page refresh (F5) keeps the
    // user's current layout instead of reverting to the default saved preference.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const persistChartTypes = () => writeSessionSnapshot({ chartTypes: getDashboardChartTypes() });
        const persistVisibility = () => writeSessionSnapshot({ visibleWidgets: getDashboardVisibleWidgets() });
        const persistTitles = () => writeSessionSnapshot({ customTitles: getDashboardCustomTitles() });
        window.addEventListener('dashboard-chart-types-applied', persistChartTypes);
        window.addEventListener('dashboard-visible-widgets-applied', persistVisibility);
        window.addEventListener('dashboard-custom-titles-applied', persistTitles);
        return () => {
            window.removeEventListener('dashboard-chart-types-applied', persistChartTypes);
            window.removeEventListener('dashboard-visible-widgets-applied', persistVisibility);
            window.removeEventListener('dashboard-custom-titles-applied', persistTitles);
        };
    }, []);

    // Persist widget order changes (drag/reorder) so refresh keeps the same order.
    useEffect(() => {
        if (widgetOrder && typeof widgetOrder === 'object' && Object.keys(widgetOrder).length > 0) {
            writeSessionSnapshot({ widgetOrder });
        }
    }, [widgetOrder]);

    // Persist selected preset id and View As role so the dropdowns survive page refresh.
    useEffect(() => {
        if (selectedPresetId !== null && selectedPresetId !== undefined) {
            writeSessionSnapshot({ selectedPresetId });
        }
    }, [selectedPresetId]);

    useEffect(() => {
        if (viewAsRole !== null && viewAsRole !== undefined) {
            writeSessionSnapshot({ viewAsRole });
        }
    }, [viewAsRole]);

    // Clear cached filters when user logs out or session changes
    useEffect(() => {
        const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const value = userDetails?.Value || {};
        // Support both lower- and PascalCase keys returned by the backend, and also
        // treat an empty/missing userDetails object as logged out.
        const currentUserId =
            value.employeeId ||
            value.EmployeeId ||
            value.userId ||
            value.UserId;
        const hasUserDetails = userDetails && Object.keys(userDetails).length > 0;

        if (!hasUserDetails && !currentUserId) {
            // User logged out
            try {
                sessionStorage.removeItem('dashboardFilters');
                sessionStorage.removeItem(DASHBOARD_SESSION_SNAPSHOT_KEY);
            } catch (e) {
                console.error('Failed to clear filters from sessionStorage:', e);
            }
        }
    }, [currentUser?.employeeId]);

    // Watch Redux filters and keep sessionStorage in sync
    useEffect(() => {
        if (filters && Object.keys(filters).length > 0) {
            try {
                // Only persist if filters have actual values (not empty defaults)
                const hasActualValues = filters.fromDate || filters.toDate || filters.companyId || filters.sellerId;
                if (hasActualValues) {
                    // Ensure dates are properly serializable
                    const filtersToSave = {
                        ...filters,
                        fromDate: filters.fromDate instanceof Date ? filters.fromDate.toISOString() : filters.fromDate,
                        toDate: filters.toDate instanceof Date ? filters.toDate.toISOString() : filters.toDate
                    };
                    sessionStorage.setItem('dashboardFilters', JSON.stringify(filtersToSave));
                    console.log('Synced filters to sessionStorage:', filtersToSave);
                }
            } catch (e) {
                console.error('Failed to sync filters to sessionStorage:', e);
            }
        }
    }, [filters]);

    const handleFilterChange = (newFilters) => {
        // Ensure EmployeeId is always included in filters
        const nextFilters = {
            ...newFilters,
            employeeId:
                newFilters?.employeeId ||
                currentUser?.employeeId ||
                currentUser?.EmployeeId ||
                0
        };

        if (JSON.stringify(filters) !== JSON.stringify(nextFilters)) {
            setHasPendingDashboardChanges(true);
            // Save filters to sessionStorage whenever they change
            try {
                sessionStorage.setItem('dashboardFilters', JSON.stringify(nextFilters));
                console.log('Saved filters to sessionStorage on change:', nextFilters);
            } catch (e) {
                console.error('Failed to save filters to sessionStorage:', e);
            }
        }

        dispatch(updateDashboardFilters(nextFilters));
    };

    const handleApplyFilters = () => {
        // Persist current filters to sessionStorage so they survive navigation
        try {
            sessionStorage.setItem('dashboardFilters', JSON.stringify(filters));
        } catch (e) {
            console.error('Failed to save filters to sessionStorage:', e);
        }
        dispatch(refreshDashboard(filters));
        setHasPendingDashboardChanges(false);
    };

    const handleCompanyChange = (companyId) => {
        // Fetch sellers for selected company
        if (companyId) {
            dispatch(fetchSellersByCompanyIds([companyId]));
        }
    };

    const openCreateModal = () => {
        setPresetModalMode('create');
        setPresetName('');
        setSetAsDefault(false);
        setShowPresetModal(true);
    };

    const openEditModal = () => {
        if (!selectedPreset) return;
        setPresetModalMode('edit');
        setPresetName(selectedPreset.Name || '');
        setSetAsDefault(Boolean(selectedPreset.IsDefault));
        setShowPresetModal(true);
    };

    const handleSavePreset = async () => {
        if (!presetName?.trim()) return;

        const trimmedName = presetName.trim();
        const currentChartTypes = getDashboardChartTypes();
        const currentVisibleWidgets = getDashboardVisibleWidgets();
        const currentCustomTitles = getDashboardCustomTitles();

        await Promise.resolve(dispatch(saveDashboardPreference(filters, trimmedName, setAsDefault, currentChartTypes, currentVisibleWidgets, widgetOrder, { customTitles: currentCustomTitles })));

        if (presetModalMode === 'edit' && selectedPreset?.Id && selectedPreset?.Name !== trimmedName) {
            await Promise.resolve(dispatch(deleteDashboardPreference(selectedPreset.Id)));
        }

        await Promise.resolve(dispatch(fetchAllDashboardPreferences()));
        setShowPresetModal(false);
    };

    const handleDeletePreset = async () => {
        if (!selectedPreset?.Id) return;

        await Promise.resolve(dispatch(deleteDashboardPreference(selectedPreset.Id)));
        const savedPreference = await Promise.resolve(dispatch(loadDefaultDashboardPreference()));
        if (savedPreference?.chartTypes) {
            applyDashboardChartTypes(savedPreference.chartTypes);
        }
        if (savedPreference?.visibleWidgets) {
            applyDashboardVisibleWidgets(savedPreference.visibleWidgets);
        }
        applyDashboardCustomTitles(savedPreference?.customTitles || {});
        if (savedPreference?.widgetOrder) {
            setWidgetOrder(savedPreference.widgetOrder);
        } else {
            setWidgetOrder({});
        }
        await Promise.resolve(dispatch(fetchAllDashboardPreferences()));
        setShowDeleteConfirm(false);
    };

    const handlePresetSelection = async (event) => {
        const value = event.target.value;
        if (!value) return;
        const presetId = Number(value);
        setSelectedPresetId(presetId);

        const preset = (preference?.allPresets || []).find((item) => item.Id === presetId);
        if (!preset) return;

        const appliedPreference = await Promise.resolve(dispatch(applyDashboardPreference(preset)));
        if (appliedPreference?.chartTypes) {
            applyDashboardChartTypes(appliedPreference.chartTypes);
        }
        if (appliedPreference?.visibleWidgets) {
            applyDashboardVisibleWidgets(appliedPreference.visibleWidgets);
        }
        applyDashboardCustomTitles(appliedPreference?.customTitles || {});
        if (appliedPreference?.widgetOrder) {
            setWidgetOrder(appliedPreference.widgetOrder);
        } else {
            setWidgetOrder({});
        }
    };

    const toggleWidgetVisibility = (widgetTitle) => {
        setLocalVisibleWidgets((prev) => ({
            ...prev,
            [widgetTitle]: !(prev[widgetTitle] !== false)
        }));
    };

    const applyWidgetCustomization = async () => {
        applyDashboardVisibleWidgets(localVisibleWidgets);
        setShowCustomizeModal(false);
        setHasPendingDashboardChanges(true);
    };

    // Phase-2 helpers

    const persistCurrentLayout = useCallback(async (order) => {
        const currentChartTypes = getDashboardChartTypes();
        const currentVisibleWidgets = getDashboardVisibleWidgets();
        const currentCustomTitles = getDashboardCustomTitles();
        const name = selectedPreset?.Name || 'Default';
        const isDefault = selectedPreset?.IsDefault !== undefined ? selectedPreset.IsDefault : true;
        await Promise.resolve(dispatch(saveDashboardPreference(filters, name, isDefault, currentChartTypes, currentVisibleWidgets, order, { customTitles: currentCustomTitles, showSuccessToast: false })));
    }, [dispatch, filters, selectedPreset]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSaveDashboardChanges = useCallback(async () => {
        await persistCurrentLayout(widgetOrderRef.current);
        setHasPendingDashboardChanges(false);
        toast.success('Dashboard changes saved.');
    }, [persistCurrentLayout]);

    // NOTE: View-As is temporarily locked to SELLER for all users until other role views are ready.
    const getRoleBasedWidgetConfig = useMemo(() => {
        const effectiveRole = viewAsRole !== null && viewAsRole !== undefined ? viewAsRole : currentUserRole;
        return getWidgetsForRole(effectiveRole);
    }, [currentUserRole, viewAsRole]);

    // Sorted visible widget config (respects widgetOrder and role)
    const sortedVisibleWidgets = useMemo(() => {
        const visible = (getRoleBasedWidgetConfig || []).filter(cfg => {
            const isTopStripKpi = cfg.type === 'kpi' && TOP_STRIP_KPI_VALUE_KEYS.has(cfg.valueKey);
            return !isTopStripKpi && getDashboardVisibleWidgets()[cfg.key] !== false;
        });
        if (Object.keys(widgetOrder).length === 0) return visible;
        return [...visible].sort((a, b) => {
            const ia = widgetOrder[a.key] !== undefined ? widgetOrder[a.key] : 999;
            const ib = widgetOrder[b.key] !== undefined ? widgetOrder[b.key] : 999;
            return ia - ib;
        });
    }, [widgetOrder, getRoleBasedWidgetConfig, TOP_STRIP_KPI_VALUE_KEYS, visibilityVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    const customizableWidgets = useMemo(() => {
        return (getRoleBasedWidgetConfig || []).filter(cfg => !(cfg.type === 'kpi' && TOP_STRIP_KPI_VALUE_KEYS.has(cfg.valueKey)));
    }, [getRoleBasedWidgetConfig, TOP_STRIP_KPI_VALUE_KEYS]);

    // Format a KPI scalar value
    const formatKpiValue = (cfg) => {
        const raw = widgets[cfg.valueKey] !== undefined && widgets[cfg.valueKey] !== null
            ? widgets[cfg.valueKey]
            : kpis[cfg.valueKey];
        switch (cfg.valueFormat) {
            case 'currency': return `$${raw?.toFixed(2) || '0.00'}`;
            case 'percent':  return `${raw?.toFixed(1) || '0.0'}%`;
            case 'duration': return `${raw?.toFixed(1) || '0.0'} min`;
            case 'hours':    return `${raw?.toFixed(1) || '0.0'} hrs`;
            default:         return raw?.toLocaleString() || '0';
        }
    };

    const handleViewAsChange = (role) => {
        setViewAsRole(role);
        const nextFilters = { ...filters, viewAsRole: role };
        if (JSON.stringify(filters) !== JSON.stringify(nextFilters)) {
            setHasPendingDashboardChanges(true);
        }
        dispatch(updateDashboardFilters(nextFilters));
    };

    const handleWidgetDrilldown = useCallback((cfg) => {
        if (!cfg?.drilldownPage) return;

        navigate(cfg.drilldownPage, {
            state: {
                fromDashboard: true,
                widgetKey: cfg.key,
                dashboardFilters: {
                    ...filters,
                    fromDate: filters?.fromDate || null,
                    toDate: filters?.toDate || null,
                    companyId: filters?.companyId || 0,
                    sellerId: filters?.sellerId || 0,
                    warehouseId: filters?.warehouseId || 0,
                    viewAsRole: viewAsRole
                }
            }
        });
    }, [filters, navigate, viewAsRole]);

    const getTopKpiDrilldown = useCallback((valueKey, fallbackPath) => {
        const matchedWidget = (getRoleBasedWidgetConfig || []).find(
            (widget) => widget.type === 'kpi' && widget.valueKey === valueKey && widget.drilldownPage
        );

        if (matchedWidget) {
            return () => handleWidgetDrilldown(matchedWidget);
        }

        if (fallbackPath) {
            return () => handleWidgetDrilldown({ key: valueKey, drilldownPage: fallbackPath });
        }

        return undefined;
    }, [getRoleBasedWidgetConfig, handleWidgetDrilldown]);

    // Get chart data for a widget config entry
    const sumDatasetData = (ds) => {
        if (!Array.isArray(ds?.data)) return 0;
        return ds.data.reduce((sum, item) => sum + (Number(item) || 0), 0);
    };

    const sumAllDatasets = (datasets) => {
        if (!Array.isArray(datasets)) return 0;
        return datasets.reduce((total, ds) => total + sumDatasetData(ds), 0);
    };

    const getChartData = (cfg) => {
        const withComputedValue = (chartData) => {
            const safeData = chartData || {};
            if (safeData.value !== undefined && safeData.value !== null) {
                return safeData;
            }

            return {
                ...safeData,
                value: sumAllDatasets(safeData.datasets),
            };
        };

        if (cfg.key === 'Daily Shipments') {
            const dailyDatasets = widgets?.dailyShipments?.datasets || [];
            return withComputedValue({
                labels: widgets?.dailyShipments?.labels || [],
                datasets: dailyDatasets,
                value: sumAllDatasets(dailyDatasets)
            });
        }
        if (cfg.type === 'table' && cfg.key === 'Recent Tasks') {
            const tasksByType = widgets?.tasksByType;
            const columns = [
                { key: 'label', header: 'Task Type' },
                { key: 'count', header: 'Count' },
            ];
            const rows = (tasksByType?.labels || []).map((label, i) => ({
                id: i,
                label,
                count: tasksByType?.datasets?.[0]?.data?.[i] ?? 0,
            }));
            return { columns, rows };
        }
        return withComputedValue(widgets[cfg.dataKey]);
    };

    // Render a single widget (chart or KPI)
    const renderWidgetContent = (cfg) => {
        const handleDrilldown = cfg.drilldownPage ? () => handleWidgetDrilldown(cfg) : undefined;
        const handleChartTypeChange = (newType) => {
            setHasPendingDashboardChanges(true);
        };
        const handleTitleRename = () => {
            setHasPendingDashboardChanges(true);
        };
        if (cfg.type === 'kpi') {
            return (
                <KPICard
                    title={cfg.key}
                    widgetKey={cfg.key}
                    editableTitle
                    onTitleChange={handleTitleRename}
                    value={formatKpiValue(cfg)}
                    icon={cfg.icon}
                    color={cfg.color}
                    loading={loading}
                    onClick={handleDrilldown}
                />
            );
        }
        return (
            <CommonChart
                title={cfg.key}
                widgetKey={cfg.key}
                editableTitle
                onTitleChange={handleTitleRename}
                subtitle={cfg.subtitle}
                defaultType={cfg.defaultChartType || (cfg.type === 'table' ? 'table' : 'bar')}
                data={getChartData(cfg)}
                loading={loading}
                onClick={handleDrilldown}
                csvExport={true}
                onTypeChange={handleChartTypeChange}
                valuePrefix={(CURRENCY_DATA_KEYS.has(cfg.dataKey) || CURRENCY_WIDGET_KEYS.has(cfg.key) || cfg.valueFormat === 'currency') ? '$' : ''}
            />
        );
    };

    // Drag-and-drop handlers
    const handleDragStart = useCallback((e, key) => {
        setDraggingKey(key);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', key);
    }, []);

    const handleDragOver = useCallback((e, key) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverKey(key);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOverKey(null);
    }, []);

    const handleDrop = useCallback((e, targetKey) => {
        e.preventDefault();
        const sourceKey = e.dataTransfer.getData('text/plain');
        if (!sourceKey || sourceKey === targetKey) {
            setDragOverKey(null);
            return;
        }
        // Build sorted key list using the current ref (latest state)
        const currentOrder = widgetOrderRef.current;
        const effectiveRole = viewAsRole !== null && viewAsRole !== undefined ? viewAsRole : currentUserRole;
        const roleWidgets = getWidgetsForRole(effectiveRole);
        const allKeys = roleWidgets.map(w => w.key);
        const sortedKeys = [...allKeys].sort((a, b) =>
            (currentOrder[a] !== undefined ? currentOrder[a] : 999) -
            (currentOrder[b] !== undefined ? currentOrder[b] : 999)
        );
        const fromIdx = sortedKeys.indexOf(sourceKey);
        const toIdx   = sortedKeys.indexOf(targetKey);
        if (fromIdx === -1 || toIdx === -1) { setDragOverKey(null); return; }

        const reordered = [...sortedKeys];
        reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, sourceKey);

        const newOrder = {};
        reordered.forEach((k, i) => { newOrder[k] = i; });

        setWidgetOrder(newOrder);
        setDraggingKey(null);
        setDragOverKey(null);
        setHasPendingDashboardChanges(true);
    }, [currentUserRole, viewAsRole]);

    const handleDragEnd = useCallback(async () => {
        setDraggingKey(null);
        setDragOverKey(null);
    }, []);

    // Reset to default layout
    const handleResetLayout = async () => {
        setWidgetOrder({});
        setHasPendingDashboardChanges(true);
        toast.success('Layout reset. Click Save Dashboard to persist.');
    };

    // Export layout JSON
    const handleExportLayout = () => {
        const layoutJson = {
            name: selectedPreset?.Name || 'Dashboard Layout',
            exportedAt: new Date().toISOString(),
            widgetOrder,
            visibleWidgets: getDashboardVisibleWidgets(),
            chartTypes: getDashboardChartTypes(),
            customTitles: getDashboardCustomTitles(),
        };
        const blob = new Blob([JSON.stringify(layoutJson, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${(layoutJson.name).replaceAll(/\s+/g, '-').toLowerCase()}-layout.json`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    // Build a human-readable filter summary for the PDF header based on the
    // currently applied dashboard filters (company, seller, date range, preset).
    const buildFilterSummary = () => {
        const lines = [];
        lines.push(`Dashboard: ${selectedPreset?.Name || 'Default Dashboard'}`);

        const companyId = filters?.companyId ? Number(filters.companyId) : null;
        lines.push(formatCompanyLine(companyId, availableCompanies));

        const sellerId = filters?.sellerId ? Number(filters.sellerId) : null;
        lines.push(formatSellerLine(sellerId, availableSellers));

        const periodLine = formatPeriodLine(
            filters?.period,
            formatDateValue(filters?.fromDate),
            formatDateValue(filters?.toDate)
        );
        if (periodLine) lines.push(periodLine);

        if (viewAsRole) {
            lines.push(`View As: ${viewAsRole}`);
        }
        return lines;
    };

    // Export the filtered dashboard view (KPIs + widgets) as a multi-page PDF.
    // The heavy lifting is delegated to the module-level PDF helpers so this
    // function stays a thin orchestrator (low cognitive complexity).
    const handleExportPdf = async () => {
        const node = exportAreaRef.current;
        if (!node) {
            toast.error('Dashboard content is not ready for export.');
            return;
        }
        if (isExportingPdf) return;

        setIsExportingPdf(true);
        const toastId = toast.info('Generating PDF...', { autoClose: false });
        try {
            const blocks = collectDashboardBlocks(node);
            if (blocks.length === 0) {
                throw new Error('No dashboard blocks found to export.');
            }
            const captured = await captureDashboardBlocks(blocks);

            const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
            const layout = createPdfLayout(pdf);
            const startY = drawPdfHeader(pdf, layout, buildFilterSummary());
            layoutCapturedBlocks(pdf, captured, startY, layout);
            addFooterPageNumbers(pdf, layout);

            pdf.save(buildPdfFilename(selectedPreset?.Name));

            toast.dismiss(toastId);
            toast.success('PDF exported successfully.');
        } catch (error) {
            toast.dismiss(toastId);
            toast.error('Failed to export PDF. Please try again.');
            // eslint-disable-next-line no-console
            console.error('Dashboard PDF export failed:', error);
        } finally {
            setIsExportingPdf(false);
        }
    };

    // Import layout JSON
    const handleImportLayout = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const layout = JSON.parse(e.target.result);
                const newOrder  = layout.widgetOrder  || {};
                setWidgetOrder(newOrder);
                if (layout.visibleWidgets) applyDashboardVisibleWidgets(layout.visibleWidgets);
                if (layout.chartTypes)     applyDashboardChartTypes(layout.chartTypes);
                if (layout.customTitles)   applyDashboardCustomTitles(layout.customTitles);
                setHasPendingDashboardChanges(true);
                toast.success('Layout imported. Click Save Dashboard to persist.');
            } catch {
                toast.error('Invalid layout file - could not import.');
            }
        };
        reader.readAsText(file);
        event.target.value = '';
    };

    // Prevent rendering if dashboard is not enabled
    if (!isEnabled) {
        return null;
    }

    return (
        <DashboardErrorBoundary>
            <div className="dashboard-container">
                <Container fluid>
                {/* Page Header */}
                <div className="dashboard-page-header">
                    <div className="dashboard-page-heading-block">
                        <div className="main__tile_heading">Dashboard</div>
                        <div className="dashboard-subtitle">{selectedPreset?.Name || 'Default Dashboard'}</div>
                    </div>
                    <div className="dashboard-page-actions">
                        <Button
                            variant="primary"
                            className="btn-apply dashboard-page-actions__save-btn"
                            onClick={handleSaveDashboardChanges}
                            disabled={!hasPendingDashboardChanges}
                        >
                            Save Dashboard
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <DashboardFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    companies={availableCompanies}
                    sellers={availableSellers}
                    warehouses={[]}
                    onCompanyChange={handleCompanyChange}
                    companiesLoading={companiesLoading}
                    companiesError={companiesError}
                    presets={preference?.allPresets || []}
                    selectedPresetId={selectedPresetId}
                    onPresetChange={handlePresetSelection}
                    canModifySelectedPreset={canModifySelectedPreset}
                    onCreateDashboard={openCreateModal}
                    onEditDashboard={openEditModal}
                    onDeleteDashboard={() => setShowDeleteConfirm(true)}
                    onCustomizeWidgets={() => setShowCustomizeModal(true)}
                    onResetLayout={handleResetLayout}
                    onExportLayout={handleExportLayout}
                    onImportLayout={() => importInputRef.current?.click()}
                    onExportPdf={handleExportPdf}
                    isExportingPdf={isExportingPdf}
                    lockCompanyFilter={isSellerScopedUser}
                    lockSellerFilter={isSellerScopedUser}
                    userRole={currentUserRole || ROLE_TYPES.SELLER}
                    viewAsRole={viewAsRole}
                    onViewAsChange={handleViewAsChange}
                    onApplyClick={handleApplyFilters}
                    hasPendingChanges={hasPendingDashboardChanges}
                />

                <div ref={exportAreaRef} className="dashboard-export-area">
                {/* KPI Cards */}
                <Row className="kpi-strip gx-2 gx-xl-3">
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <KPICard
                            title="Total Labels"
                            widgetKey="Total Labels"
                            editableTitle
                            onTitleChange={() => setHasPendingDashboardChanges(true)}
                            value={kpis.totalLabels?.toLocaleString() || '0'}
                            icon={"\u{1F4E6}"}
                            color="#1565C0"
                            changePercentage={kpis.labelsChangePercentage}
                            loading={loading}
                            onClick={getTopKpiDrilldown('totalLabels', '/labelhistory')}
                        />
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <KPICard
                            title="Outbound Orders"
                            widgetKey="Outbound Orders"
                            editableTitle
                            onTitleChange={() => setHasPendingDashboardChanges(true)}
                            value={kpis.outboundOrders?.toLocaleString() || '0'}
                            icon={"\u{1F4E4}"}
                            color="#FF6B3C"
                            changePercentage={kpis.ordersChangePercentage}
                            loading={loading}
                            onClick={getTopKpiDrilldown('outboundOrders', '/outbound')}
                        />
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <KPICard
                            title="Total SKUs"
                            widgetKey="Total SKUs"
                            editableTitle
                            onTitleChange={() => setHasPendingDashboardChanges(true)}
                            value={kpis.totalSKUs?.toLocaleString() || '0'}
                            icon={"\u{1F4CA}"}
                            color="#7B61FF"
                            changePercentage={kpis.skUsChangePercentage}
                            loading={loading}
                            onClick={getTopKpiDrilldown('totalSKUs', '/inventory')}
                        />
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <KPICard
                            title="Inbound Consignments"
                            widgetKey="Inbound Consignments"
                            editableTitle
                            onTitleChange={() => setHasPendingDashboardChanges(true)}
                            value={kpis.inboundConsignments?.toLocaleString() || '0'}
                            icon={"\u{1F4E5}"}
                            color="#00C896"
                            changePercentage={kpis.inboundChangePercentage}
                            loading={loading}
                            onClick={getTopKpiDrilldown('inboundConsignments', '/inbound')}
                        />
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <KPICard
                            title="Open Tasks"
                            widgetKey="Open Tasks"
                            editableTitle
                            onTitleChange={() => setHasPendingDashboardChanges(true)}
                            value={kpis.openTasks?.toLocaleString() || '0'}
                            icon={"\u2713"}
                            color="#FFC043"
                            changePercentage={kpis.tasksChangePercentage}
                            loading={loading}
                            onClick={getTopKpiDrilldown('openTasks', '/tasks')}
                        />
                    </Col>
                    <Col lg={2} md={4} sm={6} className="mb-3">
                        <KPICard
                            title="Wallet Balance"
                            widgetKey="Wallet Balance"
                            editableTitle
                            onTitleChange={() => setHasPendingDashboardChanges(true)}
                            value={`$${kpis.walletBalance?.toLocaleString(undefined, { 
                                minimumFractionDigits: 2, 
                                maximumFractionDigits: 2 
                            }) || '0.00'}`}
                            icon={"\u{1F4B0}"}
                            color="#2E7D32"
                            loading={loading}
                            compactValue={true}
                        />
                    </Col>
                </Row>

                {/* ====================================================================== */}
                {/* DYNAMIC WIDGET GRID - Phase-2: drag-to-reorder */}
                {/* ====================================================================== */}
                {/* hidden file input for Import Layout */}
                <input
                    ref={importInputRef}
                    type="file"
                    accept=".json,application/json"
                    style={{ display: 'none' }}
                    onChange={handleImportLayout}
                />

                <Row className="chart-row widget-grid-row" style={{ flexWrap: 'wrap' }}>
                    {sortedVisibleWidgets.map((cfg, idx) => {
                        const prevCfg = idx > 0 ? sortedVisibleWidgets[idx - 1] : null;
                        const showCategoryHeader = !prevCfg || prevCfg.category !== cfg.category;
                        const isDragging = draggingKey === cfg.key;
                        const isDragTarget = dragOverKey === cfg.key;
                        return (
                            <React.Fragment key={cfg.key}>
                                {showCategoryHeader && (
                                    <div className="w-100 category-break">
                                        <h4 className="category-header">{CATEGORY_LABELS[cfg.category]}</h4>
                                    </div>
                                )}
                                <Col
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    className={`mb-3 widget-col ${isDragging ? 'widget-dragging' : ''} ${isDragTarget ? 'widget-drag-over' : ''}`}
                                    draggable
                                    onDragStart={e => handleDragStart(e, cfg.key)}
                                    onDragOver={e => handleDragOver(e, cfg.key)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={e => handleDrop(e, cfg.key)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <div className="widget-drag-handle" title="Drag to reorder">{"\u22EE\u22EE"}</div>
                                    {renderWidgetContent(cfg)}
                                </Col>
                            </React.Fragment>
                        );
                    })}
                </Row>

                {/* Phase-3 KPI Entity Widgets - static sections */}
                {widgets.customerRepeatOrderRate && (
                    <Row className="chart-row">
                        <Col lg={3} md={6} className="mb-3">
                            <KPICard title="Repeat Order Rate" value={`${widgets.customerRepeatOrderRate.RepeatOrderRate?.toFixed(2) || '0.00'}%`} icon={"\u21BB"} color="#FF6B6B" loading={loading} />
                        </Col>
                        <Col lg={3} md={6} className="mb-3">
                            <KPICard title="Total Customers" value={widgets.customerRepeatOrderRate.TotalCustomers?.toLocaleString() || '0'} icon={"\u{1F465}"} color="#4ECDC4" loading={loading} />
                        </Col>
                        <Col lg={3} md={6} className="mb-3">
                            <KPICard title="Repeat Customers" value={widgets.customerRepeatOrderRate.RepeatCustomers?.toLocaleString() || '0'} icon={"\u2605"} color="#95E1D3" loading={loading} />
                        </Col>
                        <Col lg={3} md={6} className="mb-3">
                            <CommonChart
                                title="Order Frequency Distribution"
                                subtitle="Customer purchase patterns"
                                defaultType="bar"
                                data={{
                                    labels: [widgets.customerRepeatOrderRate.Frequency1 || '1 Order', widgets.customerRepeatOrderRate.Frequency2 || '2-3 Orders', widgets.customerRepeatOrderRate.Frequency3 || '4-9 Orders', widgets.customerRepeatOrderRate.Frequency4 || '10+ Orders'],
                                    datasets: [{ label: 'Customers', data: [widgets.customerRepeatOrderRate.Count1 || 0, widgets.customerRepeatOrderRate.Count2 || 0, widgets.customerRepeatOrderRate.Count3 || 0, widgets.customerRepeatOrderRate.Count4 || 0], backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'], borderWidth: 1 }]
                                }}
                                loading={loading}
                            />
                        </Col>
                    </Row>
                )}

                {widgets.orderValueDistribution && (
                    <Row className="chart-row">
                        <Col lg={12} className="mb-3">
                            <div className="kpi-entity-card">
                                <h5>Order Value Distribution (Quartile Analysis)</h5>
                                <Row className="mt-3">
                                    {[['Min Value', widgets.orderValueDistribution.MinValue], ['Q1 (25%)', widgets.orderValueDistribution.Q1], ['Median (50%)', widgets.orderValueDistribution.Median], ['Q3 (75%)', widgets.orderValueDistribution.Q3], ['Max Value', widgets.orderValueDistribution.MaxValue], ['IQR', widgets.orderValueDistribution.IQR]].map(([label, val]) => (
                                        <Col key={label} md={2} className="text-center quartile-stat">
                                            <div className="stat-label">{label}</div>
                                            <div className="stat-value">${val?.toFixed(2) || '0.00'}</div>
                                        </Col>
                                    ))}
                                </Row>
                                <Row className="mt-3">
                                    <Col md={4} className="text-center"><KPICard title="Total Orders" value={widgets.orderValueDistribution.TotalOrders?.toLocaleString() || '0'} icon={"\u{1F4E6}"} color="#667eea" loading={loading} /></Col>
                                    <Col md={4} className="text-center"><KPICard title="Outliers" value={widgets.orderValueDistribution.OutlierCount?.toLocaleString() || '0'} icon={"\u26A0"} color="#f093fb" loading={loading} /></Col>
                                    <Col md={4} className="text-center"><KPICard title="Outlier %" value={`${widgets.orderValueDistribution.OutlierPercent?.toFixed(2) || '0.00'}%`} icon={"\u{1F4C8}"} color="#4facfe" loading={loading} /></Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                )}

                {widgets.inventoryTurnoverVelocity && (
                    <Row className="chart-row">
                        <Col lg={3} md={6} className="mb-3"><KPICard title="Current Week Turnover" value={widgets.inventoryTurnoverVelocity.CurrentWeekTurnover?.toLocaleString() || '0'} icon={"\u{1F4CA}"} color="#00d2ff" loading={loading} /></Col>
                        <Col lg={3} md={6} className="mb-3"><KPICard title="Moving Avg Turnover" value={widgets.inventoryTurnoverVelocity.MovingAvgTurnover?.toLocaleString() || '0'} icon={"\u{1F4C8}"} color="#3a7bd5" loading={loading} /></Col>
                        <Col lg={3} md={6} className="mb-3"><KPICard title="Growth Rate" value={`${widgets.inventoryTurnoverVelocity.GrowthRate?.toFixed(2) || '0.00'}%`} icon={"\u{1F680}"} color={widgets.inventoryTurnoverVelocity.GrowthRate >= 0 ? '#00f260' : '#ff6b6b'} loading={loading} /></Col>
                        <Col lg={3} md={6} className="mb-3"><KPICard title="Total Orders (Week)" value={widgets.inventoryTurnoverVelocity.TotalOrders?.toLocaleString() || '0'} icon={"\u{1F4E6}"} color="#667eea" loading={loading} /></Col>
                    </Row>
                )}

                {widgets.orderFulfillmentCycleTime && (
                    <Row className="chart-row">
                        <Col lg={12} className="mb-3">
                            <div className="kpi-entity-card">
                                <h5>Order Fulfillment Cycle Time (Hours)</h5>
                                <Row className="mt-3">
                                    <Col md={4} sm={6} className="mb-3"><KPICard title="Avg Cycle Time" value={`${widgets.orderFulfillmentCycleTime.AvgCycleTimeHours?.toFixed(1) || '0.0'} hrs`} icon={"\u23F1"} color="#667eea" loading={loading} /></Col>
                                    <Col md={4} sm={6} className="mb-3"><KPICard title="Avg Stage 1" value={`${widgets.orderFulfillmentCycleTime.AvgStage1Hours?.toFixed(1) || '0.0'} hrs`} icon={"\u2460"} color="#4facfe" loading={loading} /></Col>
                                    <Col md={4} sm={6} className="mb-3"><KPICard title="Avg Stage 2" value={`${widgets.orderFulfillmentCycleTime.AvgStage2Hours?.toFixed(1) || '0.0'} hrs`} icon={"\u2461"} color="#00f2fe" loading={loading} /></Col>
                                </Row>
                                <Row>
                                    <Col md={4} sm={6} className="mb-3"><KPICard title="Min Cycle Time" value={`${widgets.orderFulfillmentCycleTime.MinCycleHours?.toFixed(1) || '0.0'} hrs`} icon={"\u26A1"} color="#00c851" loading={loading} /></Col>
                                    <Col md={4} sm={6} className="mb-3"><KPICard title="Max Cycle Time" value={`${widgets.orderFulfillmentCycleTime.MaxCycleHours?.toFixed(1) || '0.0'} hrs`} icon={"\u231B"} color="#ff4444" loading={loading} /></Col>
                                    <Col md={4} sm={6} className="mb-3"><KPICard title="Orders Processed" value={widgets.orderFulfillmentCycleTime.OrdersProcessed?.toLocaleString() || '0'} icon={"\u{1F4E6}"} color="#aa66cc" loading={loading} /></Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                )}
                </div>

                <Modal show={showPresetModal} onHide={() => setShowPresetModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{presetModalMode === 'create' ? 'Create Dashboard' : 'Edit Dashboard'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Dashboard Name</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={100}
                                value={presetName}
                                onChange={(event) => setPresetName(event.target.value)}
                                placeholder="Enter dashboard name"
                            />
                        </Form.Group>
                        <Form.Check
                            type="checkbox"
                            id="set-default-dashboard"
                            label="Set as default dashboard"
                            checked={setAsDefault}
                            onChange={(event) => setSetAsDefault(event.target.checked)}
                        />
                        <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
                            Current filters, chart types, and visible widgets will be saved.
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPresetModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleSavePreset} disabled={!presetName?.trim() || preference?.saving}>
                            {(() => {
                                if (preference?.saving) return 'Saving...';
                                return presetModalMode === 'create' ? 'Save Dashboard' : 'Update Dashboard';
                            })()}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Dashboard</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Delete dashboard "{selectedPreset?.Name}"? This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                        <Button variant="danger" onClick={handleDeletePreset}>Delete</Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showCustomizeModal} onHide={() => setShowCustomizeModal(false)} size="xl" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Customize Widgets</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                        <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                            Toggle widget visibility for this dashboard.
                        </p>
                        <Row>
                            {customizableWidgets.map((cfg) => {
                                const isVisible = localVisibleWidgets[cfg.key] !== false;
                                return (
                                    <Col md={6} key={cfg.key} className="mb-3">
                                        <div className="customize-widget-row">
                                            <Form.Check
                                                type="checkbox"
                                                id={`widget-${cfg.key.replaceAll(/[^a-zA-Z0-9]/g, '-')}`}
                                                label={cfg.key}
                                                checked={isVisible}
                                                onChange={() => toggleWidgetVisibility(cfg.key)}
                                                className="customize-widget-toggle"
                                            />
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCustomizeModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={applyWidgetCustomization}>Apply</Button>
                    </Modal.Footer>
                </Modal>
            </Container>
            </div>
        </DashboardErrorBoundary>
    );
};

Dashboard.propTypes = {
    isEnabled: PropTypes.bool,
};

export default Dashboard;
